import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * ChartTooltip — the one tooltip every chart shows (T-0241).
 *
 * Mirrors the prototype's `#tip` (health-readiness-v2.css): an ink-900 card
 * with the value large, the title under it and a muted sub-line, placed
 * 14px right of the pointer and above it, flipping left or below at the
 * viewport edge.
 *
 * One at a time: a tiny module store holds the tooltip's owner, content and
 * point; each chart renders `<ChartTooltip tip={…}/>` with what
 * `useChartTooltip` hands it, which is null unless that chart owns the tip.
 *
 * Content is text: the three lines are React children (text nodes), never
 * HTML. Keyboard focus on a mark shows the same tooltip as hover.
 */

export interface TipContent {
  title: string;
  value?: string | null;
  sub?: string | null;
}

interface TipState {
  owner: string;
  content: TipContent;
  x: number;
  y: number;
}

let current: TipState | null = null;
const listeners = new Set<() => void>();

function publish(next: TipState | null) {
  current = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => current;
const getServerSnapshot = () => null;

export interface TipPoint {
  x: number;
  y: number;
}

/** The top-centre of an element, where a focused mark's tooltip points. */
export function anchorOf(el: Element): TipPoint {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top };
}

function pointOf(e: React.PointerEvent): TipPoint {
  return { x: Number(e.clientX) || 0, y: Number(e.clientY) || 0 };
}

export interface TipHandlers {
  onPointerEnter: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerLeave: () => void;
  onFocus: (e: React.FocusEvent) => void;
  onBlur: () => void;
}

export function useChartTooltip() {
  const owner = React.useId();
  const state = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const tip = state && state.owner === owner ? state : null;

  const show = React.useCallback(
    (content: TipContent, point: TipPoint) => publish({ owner, content, ...point }),
    [owner],
  );
  const hide = React.useCallback(() => {
    if (current?.owner === owner) publish(null);
  }, [owner]);

  React.useEffect(() => hide, [hide]);

  /** Hover and focus handlers for one mark. */
  const bind = React.useCallback(
    (content: TipContent): TipHandlers => ({
      onPointerEnter: (e) => show(content, pointOf(e)),
      onPointerMove: (e) => show(content, pointOf(e)),
      onPointerLeave: hide,
      onFocus: (e) => show(content, anchorOf(e.currentTarget)),
      onBlur: hide,
    }),
    [show, hide],
  );

  return { tip, show, hide, bind };
}

/**
 * Arrow-key reading of a series chart (trend, spend): focus selects the
 * last point, Left/Right/Home/End move, blur clears. The chart turns the
 * index into its crosshair and tooltip.
 */
export function useSeriesKeys(count: number, onChange: (index: number | null) => void) {
  const [index, setIndex] = React.useState<number | null>(null);
  const move = (next: number | null) => {
    setIndex(next);
    onChange(next);
  };
  return {
    index,
    setIndex: move,
    handlers: {
      onFocus: () => move(count ? count - 1 : null),
      onBlur: () => move(null),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (!count) return;
        const at = index ?? count - 1;
        let next: number | null = null;
        if (e.key === 'ArrowLeft') next = Math.max(0, at - 1);
        else if (e.key === 'ArrowRight') next = Math.min(count - 1, at + 1);
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = count - 1;
        if (next === null) return;
        e.preventDefault();
        move(next);
      },
    },
  };
}

export function ChartTooltip({
  tip,
}: {
  tip: { content: TipContent; x: number; y: number } | null;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !tip) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = tip.x + 14;
    if (left + w > window.innerWidth - 8) left = tip.x - w - 14;
    let top = tip.y - h - 12;
    if (top < 8) top = tip.y + 18;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  });

  if (!tip || typeof document === 'undefined') return null;
  const { title, value, sub } = tip.content;
  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      data-chart-tooltip=""
      className="chart-tip pointer-events-none fixed z-50 max-w-[260px] rounded-card bg-ink-900 px-2.5 py-2 text-[12px] leading-[17px] text-paper-0 shadow-modal"
      style={{ left: tip.x + 14, top: tip.y }}
    >
      {value ? <span className="block text-[15px] font-semibold">{value}</span> : null}
      <span className="chart-tip-title block">{title}</span>
      {sub ? <span className="chart-tip-sub mt-0.5 block text-[11px]">{sub}</span> : null}
    </div>,
    document.body,
  );
}
